import { LineRecord, NodeState, SetOperation } from "@/components/FlowSheetEditor/services/modal"
import { CheckOutlined, DeleteOutlined, FilterOutlined, PlusOutlined, ReloadOutlined } from "@ant-design/icons"
import { Button, Input, Space, Table } from "antd"
import { useContext, useEffect, useMemo, useRef, useState } from "react"
import { VariableEditor } from "./VariableEditor"
import { IManualInputVar, getUnitAttributes } from "./attributes"
import { useGetUnitEntity } from "@/pages/Project/hooks/useUnitEntity"
import { UnitSelect, UnitStates } from "@/components/FlowSheetEditor/components/UnitSelect/UnitSelect"
import { CompositionCellEditor } from "./CompositionCellEditor"
import { getSimUnitData } from "@/services/project/simulation.service"
import { getAttributeValueFromData, getValueFromManual } from "@/components/FlowSheetEditor/components/RightPanel/UnitPanel/components/common/utils"
import { useGetDisplayValue } from "../useGetDisplayValue"
import { getFlowSimulationSelector } from "@/store/flowsheet/flowsheet.slice"
import { useSelector } from "react-redux"
import { useGetUnitComponents } from "@/components/FlowSheetEditor/hooks/useGetUnitComponents"
import { EquipmentType } from "@/types/common"
import { ChemicalComponent } from "@/services/chemical-component"
import { UnitEntity } from "@/services/unitSet/unitSet.model"

interface VariableTableProps {
  value: any
  units: UnitStates
  onChange?: (val: any) => void
  hasCreate?: boolean
}
interface FilterArgs {
  unit?: string
  variable?: string
}

export const VariableTable = (props: VariableTableProps) => {
  const { value: initData, units, onChange, hasCreate = true } = props
  const flowSimulation = useSelector(getFlowSimulationSelector)
  const [filterArgs, setFilterArgs] = useState<FilterArgs>({})
  const [editRowIndex, setEditRowIndex] = useState<number>(-1)
  const [data, setData] = useState(initData)
  const getUnitEntity = useGetUnitEntity()
  const getDisplayValue = useGetDisplayValue()
  const getUnitComponents = useGetUnitComponents()
  const componentRef = useRef<any>({})

  useEffect(() => {
    setData(initData)
  }, [initData])

  const unitMap = useMemo(() => {
    const unitMap = new Map<string, NodeState | LineRecord | SetOperation>();
    [...units.nodes, ...units.lines, ...units.operations].forEach(item => {
      unitMap.set(item.id, item)
    })

    return unitMap
  }, [units])

  const filteredData = useMemo(() => {
    let toFilterData = data
    if (filterArgs.unit) {
      toFilterData = toFilterData.filter(item => {
        const unit = unitMap.get(item.unitId)
        return unit?.tag.toLowerCase().includes((filterArgs.unit || '').toLowerCase())
      })
    }
    if (filterArgs.variable) {
      toFilterData = toFilterData.filter(rowData => {
        const unit = unitMap.get(rowData.unitId)
        const attributes = unit ? getUnitAttributes(unit) : [];
        if (attributes.length === 0) {
          return false
        }
        const attrName = attributes.find(item => item.value === rowData.attribute)?.label || ''
        return attrName.toLowerCase().includes((filterArgs.variable || '').toLowerCase())
      })
    }
    return toFilterData
  }, [data, filterArgs, unitMap])

  const toAdd = () => {
    const newRow = {} as any
    const newData = [...data, newRow]
    setData(newData)
    setEditRowIndex(newData.length - 1)
  }

  const onRowValueChange = (rowValue, oldRow, index = editRowIndex) => {
    const {attribute} = rowValue
    if (!attribute) {
      return
    }
    updateData(rowValue, oldRow, index)
  }

  const updateData = (rowValue, oldRow, index = editRowIndex) => {
    const newRow = {...oldRow, ...rowValue}
    const newData = [...data]
    newData[index] = newRow
    setData(newData)
    onChange?.(newData)
  }

  const removeByIndex = (index: number) => {
    const newData = [...data]
    newData.splice(index, 1)
    setData(newData)
    onChange?.(newData)
  }

  const onFilterChange = (type: string, value: string) => {
    setFilterArgs({
      ...filterArgs,
      [type]: value
    })
  }

  const changeUnit = (record: any, unitId: string) => {
    record.unitId = unitId
    setData([...data])
  }

  const reloadRowData = async (record, index) => {
    const unitData = await getSimUnitData({sId: flowSimulation?.id, id: record.unitId})
    const attribute = record.attribute;
    const value = getValueFromManual(getAttributeValueFromData(unitData.vars, attribute))
    const newRecord = {...record, value}
    newRecord.displayValue = getDisplayValue(units, newRecord)
    updateData(newRecord, record, index)
  }

  const getMoreAttributes = async (unit: LineRecord | NodeState | SetOperation) => {
    const baseAttrs = getUnitAttributes(unit)
    if (!(unit as NodeState)?.equipment) {
      return baseAttrs
    }
    const equipment = (unit as NodeState).equipment
    if (equipment.type === EquipmentType.StreamSplitter && flowSimulation?.id) {
      let components: ChemicalComponent.Modal[] = []
      if (unit.id === componentRef.current?.unitId) {
        components = componentRef.current?.components || []
      } else {
        components = await getUnitComponents(unit as NodeState, flowSimulation?.id)
        componentRef.current = {unitId: unit.id, components}
      }
      
      return [
        ...baseAttrs,
        ...components.map((item, index) => {
          return {
            label: `${item.name}(${item.formula})`,
            dimCode: 'MOL_FRAC',
            value: `UNIT:SPLIT_$${index}`
          }
        })
      ] as IManualInputVar[]
    }

    return baseAttrs
  }
  
  const columns = [
    {
      title: <div className="flex-space-btw">
        <div>Equipment</div>
        <Input addonAfter={<FilterOutlined />} onChange={(e) => onFilterChange('unit', e.target.value)} style={{width: 180}} />
      </div>,
      dataIndex: 'unitId',
      key: 'unitId',
      render: (unitId, record, index) => {
        if (index !== editRowIndex) {
          const unit = units.nodes.find(item => item.id === unitId)
          const stream = units.lines.find(item => item.id === unitId)
          const operation = units.operations.find(item => item.id === unitId)
          if (unit) {
            return `${unit.equipment.name}:[${unit.tag}]`
          } else if (stream) {
            const lineUnit = units.lines.find(item => item.id === unitId)
            if (lineUnit) {
              return `Stream:[${lineUnit.tag || lineUnit.id.substring(0,4)}]`
            }
          } else if (operation) {
            return `Operation: [${operation.tag}]`
          }
          return unitId
        }

        return <UnitSelect style={{width: '100%'}} units={units} value={unitId} onChange={(val) => changeUnit(record, val)}/>
      },
    },
    {
      title: <div className="flex-space-btw">
        <div>Variable</div>
        <Input addonAfter={<FilterOutlined />} onChange={(e) => onFilterChange('variable', e.target.value)} style={{width: 180}} />
      </div>,
      dataIndex: 'attribute',
      key: 'attribute',
      render: (text, record, index) => {
        if (text?.endsWith('xi_$1')) {
          return <Space>
            <div>Composition[{text}]: </div>
            <div>
              <CompositionCellEditor value={record.value} unitId={record.unitId} attribute={record.attribute} onChange={
                (value) => onRowValueChange({...record, value: value}, record, index)
              } />
            </div>
          </Space>
        }
        const unit = unitMap.get(record.unitId)
        const attributePromise = unit ? getMoreAttributes(unit) : Promise.resolve([])
        return <AttributeColumn
          unit={unit}
          record={record}
          attributesPromise={attributePromise}
          isEdit={false}
          onChange={(val) => onRowValueChange(val, record)}
          onEdit={() => setEditRowIndex(index)}
          getUnitEntity={getUnitEntity}
        />
        // const attributes = unit ? getUnitAttributes(unit) : []
        // const attribute = attributes.find(item => item.value === text)
        // let attributeText = attribute?.label
        // const unitEntity = getUnitEntity(attribute?.dimCode)
        // if (unitEntity) {
        //   attributeText = `${attributeText} [${unitEntity.unit.code}]`
        // }
        // if (editRowIndex !== index) {
        //   return <Space>
        //     <div>{attributeText || text}: </div>
        //     <div className="editable-cell-value-wrap" onClick={() => setEditRowIndex(index)}>{record.displayValue ?? record.value}</div>
        //   </Space>
        // }
        // const equipment = unitMap.get(record.unitId)
        // return <>
        //   {record.unitId && <VariableEditor attributes={attributes} value={record} equipment={equipment!} onChange={(value) => onRowValueChange(value, record)} />}
        // </>
      },
    },

    {
      title: 'Actions',
      render: (text, record, index) => {
        return <Space>
          {editRowIndex === index && <Button size="small" icon={<CheckOutlined />} type="primary" onClick={() => setEditRowIndex(-1)}/>}
          <Button icon={<ReloadOutlined />} size="small" onClick={() => reloadRowData(record, index)}/>
          <Button icon={<DeleteOutlined />} size="small" danger onClick={() => removeByIndex(index)}/>
        </Space>
      },
    },
  ]

  return <div>
    <Table columns={columns} dataSource={filteredData} pagination={false} bordered />
    {hasCreate && <div><Button type="text" icon={<PlusOutlined />} onClick={toAdd}>Add</Button></div>}
  </div>
}

interface AttributeColumnProps {
  attributesPromise: Promise<IManualInputVar[]>
  isEdit: boolean
  unit?: NodeState | LineRecord | SetOperation
  record: any
  onChange: (val: any) => void
  onEdit: () => void
  getUnitEntity: (code?: string) => UnitEntity | undefined
}

function AttributeColumn(props: AttributeColumnProps) {
  const {attributesPromise, isEdit, unit, record,onChange, onEdit, getUnitEntity} = props;
  const [attributes, setAttributes] = useState<IManualInputVar[] | null>(null)
  const code = record.attribute
  useEffect(() => {
    attributesPromise.then((res) => {
      setAttributes(res)
    })
  }, [attributesPromise])
  
  if (!attributes) {
    return null;
  }

  const attribute = attributes.find(item => item.value === code)
  let attributeText = attribute?.label
  const unitEntity = getUnitEntity(attribute?.dimCode)
  if (unitEntity) {
    attributeText = `${attributeText} [${unitEntity.unit.code}]`
  }

  if (isEdit) {
    return <Space>
      <div>{attributeText || code}: </div>
      <div className="editable-cell-value-wrap" onClick={onEdit}>{record.displayValue ?? record.value}</div>
    </Space>
  }
  return <>
    {record.unitId && <VariableEditor attributes={attributes} value={record} equipment={unit!} onChange={onChange} />}
  </>
}